import './index.css'
import cn from 'clsx'
import React, { useContext, useEffect, useRef } from 'react'
import { useDrag } from 'react-dnd'
import { getEmptyImage } from 'react-dnd-html5-backend'
import isEqual from 'react-fast-compare'
import * as ItemTypes from '@disco/constants/ItemTypes'
import type { SortableContextType } from 'contexts/sortable'
import { SortableContext } from 'contexts/sortable'
type Props = {
  children: React.ReactNode
  data?: Record<string, any>
  id: number | string
  type: typeof ItemTypes[keyof typeof ItemTypes] | 'defaultType'
  disabled?: boolean
  // BUG: onDragEnd may require useRef hack to work with external variables
  onDragEnd?: () => void
  onDragStart?: () => void
  component?: React.ElementType
  componentProps?: Record<string, any>
  disableStyles?: boolean
  componentPreviewStyles?: React.CSSProperties
  disableContentWrapper?: boolean
  preview?: React.ReactNode
}

/**
 * A draggable wrapper component using react-dnd for drag and drop functionality
 * @param children - The content to be made draggable
 * @param data - Optional data object to be included with the dragged item
 * @param id - Unique identifier for the draggable item
 * @param type - The drag type (from ItemTypes constants) that determines drop compatibility
 * @param disabled - Whether dragging is disabled - defaults to false
 * @param onDragEnd - Optional callback called when dragging ends
 * @param onDragStart - Optional callback called when dragging starts
 * @param component - The React component to render as the draggable item, defaults to 'div'
 * @param componentProps - Additional props to pass to the component
 * @param disableStyles - Whether to disable default styles, defaults to false
 * @param componentPreviewStyles - Styles to apply to the preview component while dragging
 * @param disableContentWrapper - Whether to disable the Draggable-content wrapper div (for table compatibility)
 * @returns A div container with drag functionality and visual feedback during dragging
 */
const Draggable = ({
  children,
  data,
  id,
  type,
  disabled,
  onDragEnd,
  onDragStart,
  component: Component = 'div',
  componentProps = {},
  disableStyles = false,
  componentPreviewStyles = {},
  disableContentWrapper = false,
  preview,
}: Props) => {
  const ref = useRef<HTMLDivElement | null | undefined>(null)
  const { sortableId, sortableKey, onDropOutside }: SortableContextType =
    useContext(SortableContext)
  const [{ isDragging }, drag, previewRef] = useDrag(
    () => ({
      canDrag: () => !disabled,
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        if (onDropOutside) {
          const didDrop = monitor.didDrop()
          const dropResult = monitor.getDropResult<any>()

          if (!didDrop || dropResult?.sortableId !== sortableId) {
            onDropOutside()
          }
        }

        if (onDragEnd) {
          onDragEnd()
        }
      },
      isDragging: monitor => {
        // Don't show dragging state if used outside Sortable
        if (!sortableId) {
          return false
        }

        const draggedItem = monitor.getItem()
        return draggedItem.id === id
      },
      item: () => {
        if (onDragStart) {
          onDragStart()
        }

        if (ref.current) {
          const { height, width } = ref.current.getBoundingClientRect()
          const dragPreview = preview ? (
            // Use a div for custom previews to avoid table row styling
            <div
              style={{
                ...componentPreviewStyles,
              }}
            >
              {preview}
            </div>
          ) : (
            // Use original component for default dragging
            <Component
              style={{
                ...componentPreviewStyles,
                width,
                height,
              }}
            >
              {children}
            </Component>
          )
          return {
            ...data,
            id,
            height,
            width,
            preview: dragPreview,
            sortableId,
            sortableKey,
          }
        }
      },
      type,
    }),
    [data, id, sortableId, sortableKey, type, onDragEnd, onDragStart],
  )
  useEffect(() => {
    previewRef(getEmptyImage(), {
      captureDraggingState: true,
    })
  }, [previewRef])
  return (
    <Component
      {...componentProps}
      className={cn(!disableStyles && 'Draggable', componentProps.className, {
        'Draggable--dragging': !disableStyles && isDragging,
      })}
      style={{
        ...componentProps.style,
        opacity: isDragging ? 0.5 : 1,
        cursor: !disabled ? 'grab' : 'default',
      }}
      ref={drag(ref)}
    >
      {disableContentWrapper ? (
        children
      ) : (
        <div className="Draggable-content">{children}</div>
      )}
    </Component>
  )
}

export default React.memo<Props>(Draggable, isEqual)
